import { AXIOS_INSTANCE } from '@metaonia/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { UserProfile } from '@metaonia/shared';

import { getAuthMode } from '@/lib/auth/auth-mode';
import {
  devLogin,
  devLogout,
  devRefreshTokens,
  restoreDevSession,
} from '@/lib/auth/dev-bypass-auth';
import {
  ssoRedirect,
  ssoLogout,
  ssoRefreshTokens,
  restoreSsoSession,
} from '@/lib/auth/sso-auth';
import { useAuthStore } from '@/stores/auth-store';

export function useAuth() {
  const { status, user, setStatus, setUser, clear } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    setStatus('loading');
    try {
      const mode = getAuthMode();
      if (mode === 'dev-bypass') {
        const hasSession = restoreDevSession();
        if (!hasSession) {
          setStatus('unauthenticated');
          return;
        }
        await devRefreshTokens();
      } else if (mode === 'sso') {
        const hasSession = restoreSsoSession();
        if (!hasSession) {
          setStatus('unauthenticated');
          return;
        }
        await ssoRefreshTokens();
      }
      const response = await AXIOS_INSTANCE.get('/auth/me');
      setUser((response.data as { data: UserProfile }).data);
    } catch {
      setStatus('unauthenticated');
    }
  }, [setStatus, setUser]);

  const login = useCallback(
    async (domain: string) => {
      setStatus('loading');
      try {
        const mode = getAuthMode();
        if (mode === 'sso') {
          await ssoRedirect(domain);
          return;
        }
        const { user: userData } = await devLogin(domain);
        setUser(userData);
        navigate('/');
      } catch (error) {
        setStatus('unauthenticated');
        throw error;
      }
    },
    [setStatus, setUser, navigate],
  );

  const logout = useCallback(async () => {
    try {
      await AXIOS_INSTANCE.post('/auth/logout');
    } catch {
      // Continue with local cleanup even if server logout fails
    }
    const mode = getAuthMode();
    if (mode === 'sso') {
      ssoLogout();
    } else {
      devLogout();
    }
    queryClient.clear();
    clear();
    navigate('/login');
  }, [queryClient, clear, navigate]);

  return { status, user, checkAuth, login, logout };
}
